<template>
    <tr>
        <td>{{ index + 1 }}</td>
        <!-- Opções de parcelas -->
        <td>
            <ui-select-v2
                v-model="localData.type"
                :data="filteredParcelOptions"
                item-label="label"
                return-value="value"
                :force-error="errors.type"
                @input="updateItem"
            />
        </td>

        <!-- Data do Juros -->
        <td class="td-split">
            <ui-select-v2
                v-model="localData.interest_date"
                :data="proposalDatesOptions"
                item-label="label"
                return-value="value"
                :class="{'mont-diff': isMonthDiff('interest_date')}"
                :force-error="errors.interest_date"
                @input="updateItem"
            />
            <ui-input-number-stepper
                v-if="isMonthDiff('interest_date')"
                v-model="localData.interest_date_month"
                :focus="true"
                size="sm"
                placeholder="Meses"
				:minValue="-Infinity"
                :error="errors.interest_date_month"
                @input="updateItem"
            />
        </td>

        <!-- Dia do mês do juros -->
        <td>
            <ui-input
				class="inputDay"
                v-model="localData.interest_date_day"
                :disabled="isInstallmentDate('interest_date')"
                @blur="(value) => handleDayInput('interest_date', value)"
            />
        </td>

        <!-- Data do Reajuste -->
        <td class="td-split">
            <ui-select-v2
                v-model="localData.readjustment_date"
                :data="proposalDatesOptions"
                item-label="label"
                return-value="value"
				:class="{'mont-diff': isMonthDiff('readjustment_date')}"
                :force-error="errors.readjustment_date"
                @input="updateItem"
            />
            <ui-input-number-stepper
                v-if="isMonthDiff('readjustment_date')"
                v-model="localData.readjustment_date_month"
                :focus="true"
                size="sm"
                placeholder="Meses"
				:minValue="-Infinity"
                :error="errors.readjustment_date_month"
                @input="updateItem"
            />
        </td>

        <!-- Dia do mês do reajuste -->
        <td>
            <ui-input
				class="inputDay"
                v-model="localData.readjustment_date_day"
                :disabled="isInstallmentDate('readjustment_date')"
                @blur="(value) => handleDayInput('readjustment_date', value)"
            />
        </td>

        <!-- Parcela usada de referente -->
        <td>
            <ui-select-v2
                v-model="localData.installment_ref"
                :data="installmentRefSelf"
                item-label="label"
                return-value="value"
                :disabled="installmentRefSelf?.length === 0"
                :force-error="errors.installment_ref && installmentRefSelf?.length !== 0"
                @input="updateItem"
            />
        </td>

        <td>
            <ui-menu-card>
                <ui-button type="button" @click="removeItem">Excluir</ui-button>
            </ui-menu-card>
        </td>
    </tr>
</template>

<script>
import { uiMenuCard } from '@/components/ui'
import { uiButton } from '@/components/ui/buttons'
import { uiInput, uiSelectV2, uiInputNumberStepper } from '@/components/ui/form'
import { currencyBRL } from '@/filters'
import calculations from '@/mixins/calculations/salesTable'
import helpers from '@/mixins/helpers'

export default {
	name: 'ui-sales-table-item',

	filters: {
		currencyBRL,
	},

	components: {
		uiButton,
		uiInput,
		uiMenuCard,
		uiSelectV2,
		uiInputNumberStepper,
	},

	mixins: [helpers, calculations],

	props: {
		index: Number,
		data: {
			type: Object,
			required: true,
		},
		parcelOptions: {
			type: Array,
			default: () => [],
		},
		proposalDatesOptions: {
			type: Array,
			default: () => [],
		},
		installmentRef: {
			type: Array,
			default: () => [],
		},
		installmentList: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			localData: { ...this.data }, // Criando cópia local para evitar mutação direta
			uiSavedInterestDay: null,
			uiSavedReadjustmentDay: null,
			lastType: null,
			cachedOptions: [],
			isValid: true,
			errors: {
				type: false,
				interest_date: false,
				readjustment_date: false,
				installment_ref: false,
				interest_date_month: false,
				readjustment_date_month: false,
			},
		}
	},

	computed: {
		installmentRefSelf() {
			return this.parcelOptions.filter(
				(option) =>
					this.installmentList.some((item) => item.type === option.value) &&
					option.value !== this.localData.type
			)
		},
		filteredParcelOptions() {
			return [
				...this.parcelOptions.filter(
					(item) =>
						!this.installmentList.some((installment) => installment.type === item.value)
				),
				// Adiciona a opção atual para que ela não desapareça do select
				...this.parcelOptions.filter((item) => item.value === this.localData.type),
			]
		},
	},

	watch: {
		// Atualiza a cópia local se a `prop data` mudar externamente
		data: {
			handler(newData) {
				this.localData = { ...newData }
			},
			deep: true,
		},
		'localData.interest_date'(newValue, oldValue) {
			if (newValue === 'installment_date') {
				// Armazena o valor antes de alterar
				this.uiSavedInterestDay = this.localData.interest_date_day || 0
				this.localData.interest_date_day = 0
			} else if (oldValue === 'installment_date' && this.uiSavedInterestDay !== undefined) {
				// Restaura o valor original
				this.localData.interest_date_day = this.uiSavedInterestDay || 0
			}
		},
		'localData.readjustment_date'(newValue, oldValue) {
			if (newValue === 'installment_date') {
				// Armazena o valor antes de alterar
				this.uiSavedReadjustmentDay = this.localData.readjustment_date_day || 0
				this.localData.readjustment_date_day = 0
			} else if (
				oldValue === 'installment_date' &&
				this.uiSavedReadjustmentDay !== undefined
			) {
				// Restaura o valor original
				this.localData.readjustment_date_day = this.uiSavedReadjustmentDay || 0
			}
		},
	},

	mounted() {
		this.$root.$on('clear-errors', () => {
			this.errors = {
				type: false,
				interest_date: false,
				readjustment_date: false,
				installment_ref: false,
				interest_date_month: false,
				readjustment_date_month: false,
			}
		})
	},

	methods: {
		updateItem() {
			this.validateItem()
			// Emite evento para o pai
			if (JSON.stringify(this.localData) !== JSON.stringify(this.data)) {
				this.$emit('update-item', this.index, { ...this.localData })
			}
		},

		removeItem() {
			this.$emit('remove', this.index)
		},

		/*
		 * Verifica se o valor da data do juros/reajuste é 'month_diff'
		 * e libera para informar a quantidade de meses
		 */
		isMonthDiff(proposalDate) {
			return this.localData[proposalDate] === 'month_diff'
		},

		/*
		 * Verifica se o valor da data do juros/reajuste é 'installment_date'
		 * e disabilita de informar o dia
		 */
		isInstallmentDate(proposalDate) {
			return this.localData[proposalDate] === 'installment_date'
		},

		handleDayInput(field, value) {
		    // Remove qualquer letra do valor (mantém apenas números)
		    let onlyNumbers = value.replace(/\D/g, '');
		
		    // Se ficou vazio, define como 0
		    if (onlyNumbers === '') {
		        onlyNumbers = '0';
		    }
		
		    // Corrige o valor para o dia do mês
		    const correctedValue = this.correctDayOfMonth(onlyNumbers);
		
		    // Atualiza dinamicamente o campo
		    this.localData[`${field}_day`] = correctedValue;
		
		    this.updateItem();
		},


	    correctDayOfMonth(day) {
	        if (!day) return day;

	        const today = new Date();
	        const year = today.getFullYear();
	        const month = today.getMonth(); // mês atual (0-11)

	        const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

	        if (day > lastDayOfMonth) {
	            return lastDayOfMonth;
	        }
	        return day;
	    },

		validateItem() {
			this.errors = {
				type: false,
				interest_date: false,
				readjustment_date: false,
				installment_ref: false,
			}

			// Verifica se o tipo do item está definido
			let hasMissingType = false
			if (!this.localData.type) {
				hasMissingType = true
				this.errors.type = true
			}

			// Verifica se pelo menos uma data ou referência foi preenchida
			if (
				!this.localData.interest_date &&
				!this.localData.readjustment_date &&
				!this.localData.installment_ref &&
				!hasMissingType
			) {
				this.errors.interest_date = true
				this.errors.readjustment_date = true
				this.errors.installment_ref = true
			}

			// Verifica a referência mútua apenas para o item atual
			if (this.localData.installment_ref) {
				const referenced = this.installmentList.find(
					(other) => other.type === this.localData.installment_ref
				)

				if (referenced && referenced.installment_ref === this.localData.type) {
					this.errors.installment_ref = true
				}
			}

			// Valida se "month_diff" exige um valor maior que zero nos meses
			if (
				this.localData.interest_date === 'month_diff' &&
				(this.localData.interest_date_month === 0)
			) {
				this.errors.interest_date_month = true
			}

			if (
				this.localData.readjustment_date === 'month_diff' &&
				(this.localData.readjustment_date_month === 0)
			) {
				this.errors.readjustment_date_month = true
			}
		},
	},
}
</script>

<style lang="stylus" scoped>
@import './styles.styl'
</style>